import { useId, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { Popover } from '../tasks/linear/Popover';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Hex color rendered as a small dot (colors come from the DB). */
  color?: string;
}

/**
 * Generic multi-select dropdown (checkbox list) for filters where any subset
 * of options can be active. `value` is the selected option values, `[]` = all
 * (the first row clears the selection back to "all"). Selecting every option
 * collapses back to `[]`, mirroring CompanyMultiSelect — pass
 * `collapseFull={false}` when every option is narrower than "all" (every label
 * still leaves out the leads with no label).
 *
 * CLE-433: the label is rendered visibly above the trigger (uppercase, shared
 * `.ui-field-label` look) so multi-selects match the single-select `Select`.
 * Pass `hideLabel` for the rare compact/inline cases that keep it a11y-only.
 */
export function MultiSelect({
  label,
  hideLabel = false,
  options,
  value,
  onChange,
  allLabel,
  summaryLabel,
  collapseFull = true,
  searchable = false,
  disabled,
  className,
}: {
  /** Field label. Rendered above the trigger unless `hideLabel` is set. */
  label: string;
  /** Visually hide the label and expose it as aria-label instead. */
  hideLabel?: boolean;
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  /** Text shown when nothing is selected (= all) and for the "all" row. */
  allLabel: string;
  /** Trigger summary for 3+ selections, e.g. `(n) => \`${n} selectate\``. */
  summaryLabel: (count: number) => string;
  /** Treat selecting every option as "all". Defaults to true. */
  collapseFull?: boolean;
  /** Show a search box above the options. */
  searchable?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const labelId = `${useId()}-label`;
  // Drop stale values that no longer exist among the options.
  const selected = value.filter((v) => options.some((o) => o.value === v));
  const allSelected = selected.length === 0;

  const summary = allSelected
    ? allLabel
    : selected.length <= 2
      ? options
          .filter((o) => selected.includes(o.value))
          .map((o) => o.label)
          .join(', ')
      : summaryLabel(selected.length);

  function toggle(v: string) {
    if (selected.includes(v)) {
      onChange(selected.filter((x) => x !== v));
    } else {
      const next = [...selected, v];
      // Selecting every option is the same as "all" — collapse back to [].
      onChange(collapseFull && next.length === options.length ? [] : next);
    }
  }

  return (
    <div className={className}>
      {!hideLabel && (
        <span id={labelId} className="ui-field-label">
          {label}
        </span>
      )}
      <Popover
        width={220}
        matchTriggerWidth
        trigger={({ open, toggle: togglePopover, ref }) => (
          <button
            type="button"
            ref={(el) => ref(el)}
            onClick={togglePopover}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-labelledby={hideLabel ? undefined : labelId}
            aria-label={hideLabel ? label : undefined}
            className="ui-select-trigger disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="truncate">{summary}</span>
            <ChevronDown size={14} className="ml-auto shrink-0 opacity-60" />
          </button>
        )}
      >
        {() => (
          <OptionList
            options={options}
            selected={selected}
            allLabel={allLabel}
            searchLabel={label}
            searchable={searchable}
            onClear={() => onChange([])}
            onToggle={toggle}
          />
        )}
      </Popover>
    </div>
  );
}

// Mounted only while the popover is open, so the search resets on close.
function OptionList({
  options,
  selected,
  allLabel,
  searchLabel,
  searchable,
  onClear,
  onToggle,
}: {
  options: MultiSelectOption[];
  selected: string[];
  allLabel: string;
  searchLabel: string;
  searchable: boolean;
  onClear: () => void;
  onToggle: (value: string) => void;
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const visible = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  return (
    <>
      {searchable && (
        <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
          <Search size={14} className="pointer-events-none shrink-0 text-gray-400" />
          <input
            type="search"
            name="firmio-picker-search"
            autoComplete="off"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchLabel}
            aria-label={searchLabel}
            className="w-full bg-transparent text-sm text-foreground placeholder:text-gray-400 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
          />
        </div>
      )}
      <div className="max-h-72 overflow-y-auto py-1" role="listbox" aria-multiselectable>
        {!q && (
          <>
            <button
              type="button"
              role="option"
              aria-selected={selected.length === 0}
              onClick={onClear}
              className="ui-menu-item justify-between"
            >
              <span className="truncate">{allLabel}</span>
              {selected.length === 0 && <Check size={14} className="shrink-0 text-gray-400" />}
            </button>
            <div className="my-1 border-t border-gray-200" />
          </>
        )}
        {visible.map((option) => {
          const isOn = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={isOn}
              onClick={() => onToggle(option.value)}
              className="ui-menu-item justify-between"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                    isOn ? 'border-brand-600 bg-brand-600 text-white' : 'border-gray-300'
                  }`}
                >
                  {isOn && <Check size={12} />}
                </span>
                {option.color && (
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: option.color }}
                  />
                )}
                <span className="truncate">{option.label}</span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}
